/**
 * TR-UI 1 - Ticket Resolver Dashboard v1
 * User interface for assigned staff to view and resolve tickets
 * Features: 5 sections - Total, New Today, In Progress, Completed, Resolved
 * Unified Resolver-Creator: Includes "Create New Ticket" for dual-role functionality
 * TAT: 24 hours reminder displayed
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ticketAPI, authAPI, configAPI } from '@/lib/api';
import { POLLING_INTERVALS, HTTP_STATUS, isPendingOpen } from '@/lib/constants';
import InterfaceSwitcher from '@/components/InterfaceSwitcher';
import SecondLineAssignment from '@/components/SecondLineAssignment';
import CreateTicketDialog from '@/components/CreateTicketDialog';
import { 
  ClipboardList, 
  Clock, 
  Wrench, 
  CheckCircle2, 
  AlertTriangle,
  Send,
  Upload,
  ShoppingCart,
  RefreshCw,
  LogOut,
  FileText,
  Camera,
  X,
  Plus,
  UserMinus,
  ArrowRightLeft,
  MessageSquare
} from 'lucide-react';

const TicketResolverDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [myDepartments, setMyDepartments] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    newToday: 0,
    inProgress: 0,
    diverted: 0,
    completed: 0,
    resolved: 0
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showWIPDialog, setShowWIPDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showStoreRequestDialog, setShowStoreRequestDialog] = useState(false);
  const [wipReason, setWipReason] = useState('');
  const [wipFormData, setWipFormData] = useState({
    target_date: '',
    target_time: '',
    delay_reason: '',
    material_name: '',
    material_price: '',
    send_purchase_request: false
  });
  const [completionDescription, setCompletionDescription] = useState('');
  const [completionPhoto, setCompletionPhoto] = useState(null);
  const [completionPhotoPreview, setCompletionPhotoPreview] = useState(null);
  const [storeRequest, setStoreRequest] = useState({
    item_name: '',
    quantity: '',
    urgency: 'Medium',
    description: ''
  });
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [wipLoading, setWipLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState(false);
  const [showCreateTicketDialog, setShowCreateTicketDialog] = useState(false);
  const [showOnLeaveDialog, setShowOnLeaveDialog] = useState(false);
  const [onLeaveLoading, setOnLeaveLoading] = useState(false);
  const [onLeaveFormData, setOnLeaveFormData] = useState({
    subordinate_name: '',
    subordinate_email: '',
    work_description: ''
  });
  const [showShiftCompletedDialog, setShowShiftCompletedDialog] = useState(false);
  const [shiftCompletedLoading, setShiftCompletedLoading] = useState(false);
  const [shiftCompletedFormData, setShiftCompletedFormData] = useState({
    next_shift_name: '',
    next_shift_email: '',
    comment: ''
  });

  // Delay reason options
  const DELAY_REASONS = [
    'Ordered for material purchase',
    'Busy',
    'On leave',
    'On vacation',
    'In work - need some more time'
  ];

  // Fetch user and tickets data
  const fetchData = useCallback(async () => {
    try {
      const userRes = await authAPI.getMe();
      setUser(userRes.data);
      
      // Fetch departments assigned to this user
      const deptsRes = await configAPI.getMyDepartments();
      setMyDepartments(deptsRes.data.departments || []);
      
      // Fetch tickets for this resolver
      const ticketsRes = await ticketAPI.getTickets();
      const allTickets = ticketsRes.data;
      
      // Filter tickets assigned to this user's email, in their departments,
      // OR raised by this user themselves (process_owners who file tickets
      // for other departments must still see them in their own dashboard).
      // Defensively drop "Ticket Closed" at the source — once a ticket is
      // archival-closed it must NEVER appear in any TR-UI tab regardless of
      // service-worker / cache freshness. The backend filter handles fresh
      // fetches; this guard handles state staleness right after a close.
      const myTickets = allTickets.filter(t =>
        t.status !== 'Ticket Closed' && (
          t.assigned_staff_email === userRes.data.email ||
          deptsRes.data.departments?.includes(t.department) ||
          t.requester_email === userRes.data.email
        )
      );
      
      setTickets(myTickets);
      setTicketsError(false);
      
      // Calculate stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const newToday = myTickets.filter(t => {
        const createdAt = new Date(t.created_at);
        return createdAt >= today;
      }).length;
      
      setStats({
        total: myTickets.length,
        newToday: newToday,
        inProgress: myTickets.filter(t => t.status === 'In Progress').length,
        diverted: myTickets.filter(t => t.status === 'Reassigned').length,
        completed: myTickets.filter(t => t.status === 'Completed').length,
        resolved: myTickets.filter(t => t.status === 'Resolved').length
      });
      
    } catch (error) {
      if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
        navigate('/');
      } else {
        // Keep the last known tickets on screen; polling retries automatically.
        setTicketsError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
    // Auto-refresh based on configured interval
    const interval = setInterval(fetchData, POLLING_INTERVALS.DASHBOARD_REFRESH);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      navigate('/');
    } catch (error) {
      navigate('/');
    }
  };

  // Mark ticket as Work in Progress with Smart Status Logic
  const handleMarkWIP = async () => {
    // Validate mandatory fields
    if (!wipFormData.target_date || !wipFormData.target_time) {
      toast.error('Please set expected resolution date and time');
      return;
    }
    
    if (!wipFormData.delay_reason) {
      toast.error('Please select a delay reason');
      return;
    }
    
    // If material purchase selected, validate additional fields
    if (wipFormData.delay_reason === 'Ordered for material purchase') {
      if (!wipFormData.material_name.trim()) {
        toast.error('Please enter material name');
        return;
      }
      if (!wipFormData.material_price || parseFloat(wipFormData.material_price) <= 0) {
        toast.error('Please enter a valid material price');
        return;
      }
    }
    
    // Combine date and time into ISO string
    const targetDateTime = new Date(`${wipFormData.target_date}T${wipFormData.target_time}`).toISOString();
    
    setWipLoading(true);
    try {
      const updateData = {
        status: 'In Progress',
        comment: wipReason ? `[Work in Progress] ${wipReason}` : `[Work in Progress] ${wipFormData.delay_reason}`,
        delay_reason: wipFormData.delay_reason,
        target_resolution_datetime: targetDateTime
      };
      
      // Add material purchase fields if applicable
      if (wipFormData.delay_reason === 'Ordered for material purchase') {
        updateData.material_name = wipFormData.material_name;
        updateData.material_price = parseFloat(wipFormData.material_price);
        updateData.send_purchase_request = wipFormData.send_purchase_request;
      }
      
      await ticketAPI.updateTicketWIP(selectedTicket.ticket_id, updateData);
      
      if (wipFormData.send_purchase_request && wipFormData.delay_reason === 'Ordered for material purchase') {
        toast.success('Ticket marked as WIP! Purchase request sent to Store & Accounts within 15 seconds.');
      } else {
        toast.success('Ticket marked as Work in Progress');
      }
      
      setShowWIPDialog(false);
      setWipReason('');
      setWipFormData({
        target_date: '',
        target_time: '',
        delay_reason: '',
        material_name: '',
        material_price: '',
        send_purchase_request: false
      });
      setSelectedTicket(null);
      
      // 3-second delay before refresh to ensure data is indexed
      setTimeout(() => {
        fetchData();
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ticket');
    } finally {
      setWipLoading(false);
    }
  };

  // Mark ticket as Completed and send notification
  const handleMarkComplete = async (sendNotification = false) => {
    if (!completionDescription.trim()) {
      toast.error('Please provide a completion description');
      return;
    }
    
    try {
      const updateData = {
        status: 'Resolved',
        comment: `[Resolved] ${completionDescription}`
      };
      
      // If photo is uploaded, convert to base64
      if (completionPhoto) {
        const reader = new FileReader();
        reader.onload = async () => {
          updateData.completion_photo = reader.result;
          await ticketAPI.updateTicket(selectedTicket.ticket_id, updateData);
          
          if (sendNotification) {
            await ticketAPI.sendCompletionNotification(selectedTicket.ticket_id);
            toast.success('Ticket resolved and notification sent to creator');
          } else {
            toast.success('Ticket marked as resolved');
          }
          
          setShowCompleteDialog(false);
          setCompletionDescription('');
          setCompletionPhoto(null);
          setCompletionPhotoPreview(null);
          setSelectedTicket(null);
          fetchData();
        };
        reader.readAsDataURL(completionPhoto);
      } else {
        await ticketAPI.updateTicket(selectedTicket.ticket_id, updateData);
        
        if (sendNotification) {
          await ticketAPI.sendCompletionNotification(selectedTicket.ticket_id);
          toast.success('Ticket resolved and notification sent to creator');
        } else {
          toast.success('Ticket marked as resolved');
        }
        
        setShowCompleteDialog(false);
        setCompletionDescription('');
        setSelectedTicket(null);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  // Submit store purchase request
  const handleStoreRequest = async () => {
    if (!storeRequest.item_name.trim() || !storeRequest.quantity.trim()) {
      toast.error('Please fill in item name and quantity');
      return;
    }
    
    try {
      await ticketAPI.createStoreRequest({
        ...storeRequest,
        related_ticket_id: selectedTicket?.ticket_id,
        requested_by: user.email
      });
      toast.success('Store purchase request submitted and assigned to Store Department');
      setShowStoreRequestDialog(false);
      setStoreRequest({ item_name: '', quantity: '', urgency: 'Medium', description: '' });
    } catch (error) {
      toast.error('Failed to submit store request');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCompletionPhoto(file);
      const reader = new FileReader();
      reader.onload = () => setCompletionPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setCompletionPhoto(null);
    setCompletionPhotoPreview(null);
  };

  // Handle On Leave submission
  const handleOnLeaveSubmit = async () => {
    if (!onLeaveFormData.subordinate_name.trim()) {
      toast.error('Subordinate Name is required');
      return;
    }
    if (!onLeaveFormData.subordinate_email.trim()) {
      toast.error('Subordinate Email is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(onLeaveFormData.subordinate_email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (onLeaveFormData.subordinate_email.toLowerCase() === user?.email?.toLowerCase()) {
      toast.error('Cannot assign ticket to yourself');
      return;
    }
    if (!onLeaveFormData.work_description.trim()) {
      toast.error('Work Description is required');
      return;
    }

    setOnLeaveLoading(true);
    try {
      await ticketAPI.submitOnLeave(selectedTicket.ticket_id, {
        subordinate_name: onLeaveFormData.subordinate_name,
        subordinate_email: onLeaveFormData.subordinate_email,
        work_description: onLeaveFormData.work_description
      });
      toast.success('Ticket reassigned to subordinate! Email notification sent.');
      setShowOnLeaveDialog(false);
      setOnLeaveFormData({ subordinate_name: '', subordinate_email: '', work_description: '' });
      setSelectedTicket(null);
      setTimeout(() => { fetchData(); }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit On Leave reassignment');
    } finally {
      setOnLeaveLoading(false);
    }
  };

  // Handle Shift Completed hand-over submission
  const handleShiftCompletedSubmit = async () => {
    if (!shiftCompletedFormData.next_shift_name.trim()) {
      toast.error('Next-Shift Staff Name is required');
      return;
    }
    if (!shiftCompletedFormData.next_shift_email.trim()) {
      toast.error('Next-Shift Staff Email is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shiftCompletedFormData.next_shift_email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (shiftCompletedFormData.next_shift_email.toLowerCase() === user?.email?.toLowerCase()) {
      toast.error('Cannot hand over ticket to yourself');
      return;
    }
    if (!shiftCompletedFormData.comment.trim()) {
      toast.error('Comment is required');
      return;
    }

    setShiftCompletedLoading(true);
    try {
      await ticketAPI.submitShiftCompleted(selectedTicket.ticket_id, {
        next_shift_name: shiftCompletedFormData.next_shift_name,
        next_shift_email: shiftCompletedFormData.next_shift_email,
        comment: shiftCompletedFormData.comment
      });
      toast.success('Ticket handed over to next shift! Notification sent (CC: Process Owner, L1 Admin, L2 Admin).');
      setShowShiftCompletedDialog(false);
      setShiftCompletedFormData({ next_shift_name: '', next_shift_email: '', comment: '' });
      setSelectedTicket(null);
      setTimeout(() => { fetchData(); }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to hand over shift');
    } finally {
      setShiftCompletedLoading(false);
    }
  };

  // Calculate time remaining for SLA
  const getTimeRemaining = (slaBreachTime) => {
    const now = new Date();
    const breach = new Date(slaBreachTime);
    const diff = breach - now;
    
    if (diff <= 0) return { text: 'OVERDUE', isOverdue: true };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return { 
      text: `${hours}h ${minutes}m remaining`, 
      isOverdue: false,
      isUrgent: hours < 4
    };
  };

  // Filter tickets by tab
  const getFilteredTickets = () => {
    switch (activeTab) {
      case 'new':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return tickets.filter(t => new Date(t.created_at) >= today && isPendingOpen(t));
      case 'wip':
        return tickets.filter(t => t.status === 'In Progress');
      case 'diverted':
        return tickets.filter(t => t.status === 'Reassigned');
      case 'completed':
        return tickets.filter(t => t.status === 'Resolved');
      case 'pending':
        return tickets.filter(t => t.status === 'Open' || t.status === 'Acknowledged');
      default:
        // "Active" tab — Pending & Open tickets only. Excludes terminal
        // statuses (Resolved, Closed - Feedback Received, Duplicate Ticket,
        // Completed, Ticket Closed).
        return tickets.filter(isPendingOpen);
    }
  };

  // Bold, high-contrast status badges — pastel variants were unreadable on
  // the white card background. White text on saturated backgrounds covers
  // every status this UI can render (TC UI-1 + TR UI-1 share the palette).
  const getStatusBadge = (status) => {
    const styles = {
      'Open': 'bg-blue-600 text-white',
      'Acknowledged': 'bg-cyan-600 text-white',
      'In Progress': 'bg-amber-500 text-white',
      'Reassigned': 'bg-pink-600 text-white',
      'Resolved': 'bg-green-600 text-white',
      'Completed': 'bg-emerald-600 text-white',
      'Closed - Feedback Received': 'bg-teal-600 text-white',
      'SLA BREACHED': 'bg-red-600 text-white animate-pulse',
      'Ticket Diverted': 'bg-violet-600 text-white',
      'On Leave': 'bg-orange-500 text-white',
      'Shift Completed': 'bg-indigo-600 text-white',
      'Purchase Material Requested': 'bg-fuchsia-600 text-white',
      'Purchase Material Approved': 'bg-purple-600 text-white',
      'Purchase Material Received': 'bg-emerald-700 text-white',
      'Long Term Vacations': 'bg-rose-600 text-white',
      'Ticket Closed': 'bg-slate-700 text-white',
      'Duplicate Ticket': 'bg-zinc-600 text-white'
    };
    return (
      <Badge className={`${styles[status] || 'bg-slate-700 text-white'} font-semibold shadow-sm`}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-[#E97832] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1e3a5f] to-[#0f172a] text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-[#E97832]" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold truncate">Ticket Resolver</h1>
                <p className="text-xs sm:text-sm text-slate-300 truncate">
                  {user?.name}{myDepartments.length > 0 ? ` | ${myDepartments[0]}${myDepartments.length > 1 ? ` +${myDepartments.length - 1}` : ''}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
              <Button 
                onClick={() => setShowCreateTicketDialog(true)}
                size="sm" 
                className="bg-[#E97832] hover:bg-[#C25E20] text-white text-xs sm:text-sm"
                data-testid="resolver-create-ticket-btn"
              >
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Create Ticket</span>
              </Button>
              <InterfaceSwitcher user={user} currentInterface="process_owner" />
              <Button
                data-testid="fb-nav-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/feedback')}
                className="text-white hover:bg-white/10 flex items-center gap-1"
                title="Feedback (FB)"
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden md:inline">Feedback</span>
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-white hover:bg-white/10 hidden sm:flex"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* TAT Warning Banner */}
      <div className="bg-red-600 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="font-semibold text-xs sm:text-base">
            TAT: 24 HOURS to resolve tickets. Update status if WIP.
          </span>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {ticketsError && tickets.length === 0 && (
          <div data-testid="tickets-load-error" className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-800 font-medium">
              Tickets are taking longer than usual to load. We'll keep retrying automatically.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchData}
              data-testid="tickets-retry-btn"
              className="border-amber-400 text-amber-800 hover:bg-amber-100 flex-shrink-0"
            >
              Retry now
            </Button>
          </div>
        )}
        {/* Stats Cards - 5 Sections */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 mb-6">
          {/* Section 1: Total Tickets */}
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs sm:text-sm">Total Assigned</p>
                  <p className="text-2xl sm:text-3xl font-bold">{stats.total}</p>
                </div>
                <ClipboardList className="w-7 h-7 sm:w-10 sm:h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          {/* Section 2: New Today */}
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs sm:text-sm">New Today</p>
                  <p className="text-2xl sm:text-3xl font-bold">{stats.newToday}</p>
                </div>
                <Clock className="w-7 h-7 sm:w-10 sm:h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          {/* Section 3: In Progress */}
          <Card className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white border-0 shadow-lg">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-xs sm:text-sm">In Progress</p>
                  <p className="text-2xl sm:text-3xl font-bold">{stats.inProgress}</p>
                </div>
                <Wrench className="w-7 h-7 sm:w-10 sm:h-10 text-yellow-200" />
              </div>
            </CardContent>
          </Card>

          {/* Section 4: Completed */}
          <Card className="bg-gradient-to-br from-emerald-500 to-green-600 text-white border-0 shadow-lg">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-xs sm:text-sm">Completed</p>
                  <p className="text-2xl sm:text-3xl font-bold">{stats.completed}</p>
                </div>
                <CheckCircle2 className="w-7 h-7 sm:w-10 sm:h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          {/* Section 5: Resolved */}
          <Card className="bg-gradient-to-br from-slate-600 to-slate-700 text-white border-0 shadow-lg col-span-2 sm:col-span-1">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-xs sm:text-sm">Resolved</p>
                  <p className="text-2xl sm:text-3xl font-bold">{stats.resolved}</p>
                </div>
                <FileText className="w-7 h-7 sm:w-10 sm:h-10 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Ticket List with Tabs */}
        <Card className="shadow-lg">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-bold text-[#1e3a5f]">My Assigned Tickets</CardTitle>
                <CardDescription>View and manage tickets assigned to you</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full justify-start rounded-none border-b bg-slate-50 p-0 h-auto overflow-x-auto">
                <TabsTrigger 
                  value="all" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-white px-3 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm whitespace-nowrap"
                >
                  Active ({tickets.filter(isPendingOpen).length})
                </TabsTrigger>
                <TabsTrigger 
                  value="new" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-white px-3 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm whitespace-nowrap"
                >
                  New ({stats.newToday})
                </TabsTrigger>
                <TabsTrigger 
                  value="pending" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-white px-3 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm whitespace-nowrap"
                >
                  Pending
                </TabsTrigger>
                <TabsTrigger 
                  value="wip" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-white px-6 py-3"
                >
                  In Progress ({stats.inProgress})
                </TabsTrigger>
                <TabsTrigger 
                  value="diverted" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-pink-500 data-[state=active]:bg-pink-50 px-3 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm whitespace-nowrap"
                >
                  Reassigned ({stats.diverted})
                </TabsTrigger>
                <TabsTrigger 
                  value="completed" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-white px-3 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm whitespace-nowrap"
                >
                  Resolved ({stats.resolved})
                </TabsTrigger>
              </TabsList>

              <div className="p-3 sm:p-4">
                {getFilteredTickets().length === 0 ? (
                  <div className="text-center py-8 sm:py-12 text-slate-500">
                    <ClipboardList className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-sm sm:text-base">No tickets in this category</p>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {getFilteredTickets().map((ticket) => {
                      const timeRemaining = getTimeRemaining(ticket.sla_breach_time);
                      
                      return (
                        <div 
                          key={ticket.ticket_id}
                          className={`border rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow ${
                            timeRemaining.isOverdue ? 'border-red-300 bg-red-50' : 
                            timeRemaining.isUrgent ? 'border-yellow-300 bg-yellow-50' : 
                            'border-slate-200 bg-white'
                          }`}
                        >
                          <div className="flex flex-col gap-3 sm:gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="font-bold text-sm sm:text-base text-[#1e3a5f]">{ticket.ticket_id}</span>
                                {getStatusBadge(ticket.status)}
                                <Badge variant="outline" className="text-xs">
                                  {ticket.department}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600 mb-2 line-clamp-2">
                                {ticket.description}
                              </p>
                              <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                                <span>From: {ticket.requester_name}</span>
                                <span>Contact: {ticket.contact}</span>
                                <span>Created: {new Date(ticket.created_at).toLocaleString()}</span>
                              </div>
                            </div>
                            
                            <div className="flex flex-col items-end gap-2">
                              {/* SLA Timer */}
                              <div className={`text-sm font-semibold px-3 py-1 rounded-full ${
                                timeRemaining.isOverdue ? 'bg-red-600 text-white' :
                                timeRemaining.isUrgent ? 'bg-yellow-500 text-white' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {timeRemaining.text}
                              </div>
                              
                              {/* Action Buttons */}
                              {ticket.status !== 'Resolved' && (
                                <div className="flex gap-2">
                                  {ticket.status !== 'In Progress' && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => {
                                        setSelectedTicket(ticket);
                                        setShowWIPDialog(true);
                                      }}
                                      className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                                    >
                                      <Wrench className="w-3 h-3 mr-1" />
                                      Mark WIP
                                    </Button>
                                  )}
                                  <Button 
                                    size="sm"
                                    onClick={() => {
                                      setSelectedTicket(ticket);
                                      setShowCompleteDialog(true);
                                    }}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Complete
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedTicket(ticket);
                                      setShowStoreRequestDialog(true);
                                    }}
                                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                  >
                                    <ShoppingCart className="w-3 h-3 mr-1" />
                                    Store Request
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedTicket(ticket);
                                      setShowOnLeaveDialog(true);
                                    }}
                                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                    data-testid="on-leave-btn"
                                  >
                                    <UserMinus className="w-3 h-3 mr-1" />
                                    On Leave
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedTicket(ticket);
                                      setShowShiftCompletedDialog(true);
                                    }}
                                    className="text-sky-600 border-sky-300 hover:bg-sky-50"
                                    data-testid="shift-completed-btn"
                                  >
                                    <ArrowRightLeft className="w-3 h-3 mr-1" />
                                    Shift Completed
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Second Line Assignment Section */}
                          <div className="mt-4 pt-4 border-t border-slate-100">
                            <SecondLineAssignment 
                              ticket={ticket} 
                              onAssignmentComplete={fetchData}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </main>

      {/* Work in Progress Dialog - Smart Status & Delay Logic */}
      <Dialog open={showWIPDialog} onOpenChange={setShowWIPDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">Mark as Work in Progress</DialogTitle>
            <DialogDescription>
              Ticket: {selectedTicket?.ticket_id} - {selectedTicket?.department}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Target Resolution DateTime - Mandatory */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="wip-target-date" className="text-sm font-medium">
                  Expected Resolution Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="wip-target-date"
                  type="date"
                  value={wipFormData.target_date}
                  onChange={(e) => setWipFormData(prev => ({ ...prev, target_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full"
                  data-testid="wip-target-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wip-target-time" className="text-sm font-medium">
                  Expected Resolution Time <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="wip-target-time"
                  type="time"
                  value={wipFormData.target_time}
                  onChange={(e) => setWipFormData(prev => ({ ...prev, target_time: e.target.value }))}
                  className="w-full"
                  data-testid="wip-target-time"
                />
              </div>
            </div>

            {/* Delay Reason Dropdown - Mandatory */}
            <div className="space-y-2">
              <Label htmlFor="delay-reason" className="text-sm font-medium">
                Delay Reason <span className="text-red-500">*</span>
              </Label>
              <Select
                value={wipFormData.delay_reason}
                onValueChange={(value) => setWipFormData(prev => ({ 
                  ...prev, 
                  delay_reason: value,
                  // Reset material fields if not material purchase
                  material_name: value === 'Ordered for material purchase' ? prev.material_name : '',
                  material_price: value === 'Ordered for material purchase' ? prev.material_price : '',
                  send_purchase_request: value === 'Ordered for material purchase' ? prev.send_purchase_request : false
                }))}
              >
                <SelectTrigger id="delay-reason" className="w-full" data-testid="delay-reason-select">
                  <SelectValue placeholder="Select delay reason..." />
                </SelectTrigger>
                <SelectContent>
                  {DELAY_REASONS.map((reason) => (
                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Conditional: Material Purchase Fields */}
            {wipFormData.delay_reason === 'Ordered for material purchase' && (
              <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  Material Purchase Details
                </h4>
                
                <div className="space-y-2">
                  <Label htmlFor="material-name" className="text-sm">
                    Material Name / Description <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="material-name"
                    placeholder="e.g., LED Bulb 15W, PVC Pipe 2 inch..."
                    value={wipFormData.material_name}
                    onChange={(e) => setWipFormData(prev => ({ ...prev, material_name: e.target.value }))}
                    className="w-full"
                    data-testid="material-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="material-price" className="text-sm">
                    Estimated Price (₹) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="material-price"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Enter estimated price..."
                    value={wipFormData.material_price}
                    onChange={(e) => setWipFormData(prev => ({ ...prev, material_price: e.target.value }))}
                    className="w-full"
                    data-testid="material-price-input"
                  />
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-100">
                  <input
                    type="checkbox"
                    id="send-purchase-request"
                    checked={wipFormData.send_purchase_request}
                    onChange={(e) => setWipFormData(prev => ({ ...prev, send_purchase_request: e.target.checked }))}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    data-testid="purchase-request-toggle"
                  />
                  <Label htmlFor="send-purchase-request" className="text-sm cursor-pointer flex-1">
                    <span className="font-medium">Send Purchase Request to Store & Accounts</span>
                    <p className="text-xs text-slate-500 mt-1">
                      Email will be sent within 15 seconds to Store Dept & Accounts with CC to you and admins
                    </p>
                  </Label>
                </div>
              </div>
            )}

            {/* Optional: Additional Notes */}
            <div className="space-y-2">
              <Label htmlFor="wip-reason" className="text-sm">Additional Notes (Optional)</Label>
              <Textarea
                id="wip-reason"
                placeholder="Any additional details about the work in progress..."
                value={wipReason}
                onChange={(e) => setWipReason(e.target.value)}
                rows={2}
                className="mt-1"
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Remember: TAT is 24 hours. Set a realistic target date for resolution.
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => {
              setShowWIPDialog(false);
              setWipFormData({
                target_date: '',
                target_time: '',
                delay_reason: '',
                material_name: '',
                material_price: '',
                send_purchase_request: false
              });
              setWipReason('');
            }}>Cancel</Button>
            <Button 
              onClick={handleMarkWIP} 
              disabled={wipLoading}
              className="bg-yellow-500 hover:bg-yellow-600"
            >
              {wipLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                <>
                  <Wrench className="w-4 h-4 mr-2" />
                  Update Status
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Ticket Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">Mark as Completed</DialogTitle>
            <DialogDescription>
              Ticket: {selectedTicket?.ticket_id} - {selectedTicket?.department}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="completion-desc">Completion Description <span className="text-red-500">*</span></Label>
              <Textarea
                id="completion-desc"
                placeholder="Describe the work done and resolution..."
                value={completionDescription}
                onChange={(e) => setCompletionDescription(e.target.value)}
                rows={4}
                className="mt-2"
              />
            </div>
            
            <div>
              <Label>Upload Photo (Optional)</Label>
              {!completionPhotoPreview ? (
                <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#E97832] transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    id="completion-photo"
                  />
                  <label htmlFor="completion-photo" className="cursor-pointer">
                    <Camera className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-600">Click to upload completion photo</p>
                  </label>
                </div>
              ) : (
                <div className="relative mt-2">
                  <img 
                    src={completionPhotoPreview} 
                    alt="Completion" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={removePhoto}
                    className="absolute top-2 right-2"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                <CheckCircle2 className="w-4 h-4 inline mr-1" />
                Clicking "Complete & Notify" will send an email to the ticket creator with CC to Level 1 & Level 2 admins.
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
            <Button onClick={() => handleMarkComplete(false)} variant="secondary">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Complete Only
            </Button>
            <Button onClick={() => handleMarkComplete(true)} className="bg-green-600 hover:bg-green-700">
              <Send className="w-4 h-4 mr-2" />
              Complete & Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Store Purchase Request Dialog */}
      <Dialog open={showStoreRequestDialog} onOpenChange={setShowStoreRequestDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">Store Purchase Request</DialogTitle>
            <DialogDescription>
              Request materials from Store Department for ticket: {selectedTicket?.ticket_id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="item-name">Item Name <span className="text-red-500">*</span></Label>
              <Input
                id="item-name"
                placeholder="Enter item/material name"
                value={storeRequest.item_name}
                onChange={(e) => setStoreRequest({...storeRequest, item_name: e.target.value})}
                className="mt-2"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quantity">Quantity <span className="text-red-500">*</span></Label>
                <Input
                  id="quantity"
                  placeholder="e.g., 5 units"
                  value={storeRequest.quantity}
                  onChange={(e) => setStoreRequest({...storeRequest, quantity: e.target.value})}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="urgency">Urgency</Label>
                <Select 
                  value={storeRequest.urgency} 
                  onValueChange={(v) => setStoreRequest({...storeRequest, urgency: v})}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="store-desc">Additional Details (Optional)</Label>
              <Textarea
                id="store-desc"
                placeholder="Any specifications or additional requirements..."
                value={storeRequest.description}
                onChange={(e) => setStoreRequest({...storeRequest, description: e.target.value})}
                rows={3}
                className="mt-2"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <ShoppingCart className="w-4 h-4 inline mr-1" />
                This request will be automatically assigned to the Store Department (Sandeep Singh).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStoreRequestDialog(false)}>Cancel</Button>
            <Button onClick={handleStoreRequest} className="bg-blue-600 hover:bg-blue-700">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* On Leave Dialog */}
      <Dialog open={showOnLeaveDialog} onOpenChange={setShowOnLeaveDialog}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">On Leave - Reassign Ticket</DialogTitle>
            <DialogDescription>
              Ticket: {selectedTicket?.ticket_id} - {selectedTicket?.department}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Reassigning this ticket to your subordinate. SLA timer will continue (no reset). Email notification will be sent to the subordinate with CC to you and Level 1 Admin.</span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="on-leave-sub-name" className="text-sm font-medium">
                Subordinate Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="on-leave-sub-name"
                placeholder="Enter subordinate's full name"
                value={onLeaveFormData.subordinate_name}
                onChange={(e) => setOnLeaveFormData(prev => ({ ...prev, subordinate_name: e.target.value }))}
                data-testid="on-leave-subordinate-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="on-leave-sub-email" className="text-sm font-medium">
                Subordinate Email ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="on-leave-sub-email"
                type="email"
                placeholder="subordinate@mitgurukul.com"
                value={onLeaveFormData.subordinate_email}
                onChange={(e) => setOnLeaveFormData(prev => ({ ...prev, subordinate_email: e.target.value }))}
                data-testid="on-leave-subordinate-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="on-leave-work-desc" className="text-sm font-medium">
                Work Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="on-leave-work-desc"
                placeholder="Describe pending work and instructions for the subordinate..."
                value={onLeaveFormData.work_description}
                onChange={(e) => setOnLeaveFormData(prev => ({ ...prev, work_description: e.target.value }))}
                rows={4}
                data-testid="on-leave-work-description"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => {
              setShowOnLeaveDialog(false);
              setOnLeaveFormData({ subordinate_name: '', subordinate_email: '', work_description: '' });
            }}>Cancel</Button>
            <Button 
              onClick={handleOnLeaveSubmit} 
              disabled={onLeaveLoading}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="on-leave-submit-btn"
            >
              {onLeaveLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Reassigning...
                </>
              ) : (
                <>
                  <UserMinus className="w-4 h-4 mr-2" />
                  Submit On Leave
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Completed Dialog — hand over ticket to next-shift staff sub-ordinate */}
      <Dialog open={showShiftCompletedDialog} onOpenChange={setShowShiftCompletedDialog}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">Shift Completed – Hand Over Ticket</DialogTitle>
            <DialogDescription>
              Ticket: {selectedTicket?.ticket_id} - {selectedTicket?.department}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm text-sky-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Your shift has ended. Assign this ticket to the next-shift staff sub-ordinate so work can continue. SLA timer does not reset. Email goes TO the next-shift staff with CC to you (Process Owner), Level 1 Admin, and Level 2 Admin.</span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sc-next-name" className="text-sm font-medium">
                Next-Shift Staff Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="sc-next-name"
                placeholder="Enter next-shift staff full name"
                value={shiftCompletedFormData.next_shift_name}
                onChange={(e) => setShiftCompletedFormData(prev => ({ ...prev, next_shift_name: e.target.value }))}
                data-testid="shift-completed-next-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sc-next-email" className="text-sm font-medium">
                Next-Shift Staff Email ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="sc-next-email"
                type="email"
                placeholder="nextshift@mitgurukul.com"
                value={shiftCompletedFormData.next_shift_email}
                onChange={(e) => setShiftCompletedFormData(prev => ({ ...prev, next_shift_email: e.target.value }))}
                data-testid="shift-completed-next-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sc-comment" className="text-sm font-medium">
                Comment on Ticket <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="sc-comment"
                placeholder="Summary of work done in this shift and pending items for the next shift..."
                value={shiftCompletedFormData.comment}
                onChange={(e) => setShiftCompletedFormData(prev => ({ ...prev, comment: e.target.value }))}
                rows={4}
                data-testid="shift-completed-comment"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => {
              setShowShiftCompletedDialog(false);
              setShiftCompletedFormData({ next_shift_name: '', next_shift_email: '', comment: '' });
            }}>Cancel</Button>
            <Button
              onClick={handleShiftCompletedSubmit}
              disabled={shiftCompletedLoading}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="shift-completed-submit-btn"
            >
              {shiftCompletedLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Handing Over...
                </>
              ) : (
                <>
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Hand Over to Next Shift
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog - Dual Role Functionality */}
      <CreateTicketDialog
        open={showCreateTicketDialog}
        onClose={() => setShowCreateTicketDialog(false)}
        onSuccess={() => {
          setShowCreateTicketDialog(false);
          toast.success('Ticket created! You will receive notifications as it progresses.');
          // 3-second delay before refresh
          setTimeout(() => {
            fetchData();
          }, 3000);
        }}
        user={user}
      />
    </div>
  );
};

export default TicketResolverDashboard;
